import { Button, Divider, Group, Paper, Stack, Text } from '@mantine/core';
import { createReference, MedplumClient } from '@medplum/core';
import { MantineReactTable, MRT_Row, useMantineReactTable } from 'mantine-react-table';
import { ReactElement, ReactNode } from 'react';
import { getNotificationColumns } from './table-columns';
import { getBaseTableConfig } from './table-config';
import { NotificationItem, RowActionKind, ToolbarAction, ToolbarActionContext } from './types';
import { useNotificationNavigation } from './useNotificationNavigation';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@bariendo/lib/constants';

export interface NotificationTableProps {
  medplum: MedplumClient;
  notifications: NotificationItem[];
  loading: boolean;
  loadingMore: boolean;
  rowActionKind: RowActionKind;
  toolbarActions: ToolbarAction[];
  inboxSortControl?: ReactNode;
  refresh: () => void;
  handleScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  searchTerm?: string;
  onRowClick: (
    notification: NotificationItem,
    medplum: MedplumClient,
    defaultHandler: (notification: NotificationItem) => void
  ) => void;
}

export function NotificationTable({
  medplum,
  notifications,
  loading,
  loadingMore,
  rowActionKind,
  toolbarActions,
  inboxSortControl,
  refresh,
  handleScroll,
  searchTerm = '',
  onRowClick,
}: NotificationTableProps): ReactElement {
  const { handleRowClick } = useNotificationNavigation();
  const isTasksEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_TASK_MANAGEMENT);

  const getRowBackgroundColor = (notification: NotificationItem): string => {
    if (notification.isArchived) {
      return 'white';
    }
    if (notification.isRead && !notification.isArchived) {
      return 'rgba(0, 0, 0, 0.04)';
    }
    // Unread notifications
    return 'white';
  };

  const columns = getNotificationColumns({
    action: rowActionKind,
    refresh,
    searchTerm,
  });

  const baseConfig = getBaseTableConfig();

  const table = useMantineReactTable({
    ...baseConfig,
    columns,
    data: notifications,
    state: {
      isLoading: loading,
    },
    enableSorting: false,
    enablePagination: false,
    enableBottomToolbar: false,
    getRowId: (row) => row.id,
    initialState: baseConfig.initialState,
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    mantineTableContainerProps: {
      style: {
        flex: 1,
        overflow: 'auto',
      },
      onScroll: handleScroll,
    },
    mantineTableHeadCellProps: {
      style: {
        fontWeight: '500',
      },
    },
    mantineTableProps: {
      highlightOnHover: true,
    },
    renderTopToolbar: ({ table }) => {
      const selectedRows = table.getSelectedRowModel().rows;
      const selectedNotifications = selectedRows.map((row: MRT_Row<NotificationItem>) => row.original);
      const selectedCount = selectedRows.length;

      const selectedRefs = selectedNotifications.map((notification) => createReference(notification.task));

      const handleActionComplete = () => {
        table.resetRowSelection();
        refresh();
      };

      const ctx: ToolbarActionContext = {
        medplum,
        selectedCount,
        selectedNotifications,
        selectedRefs,
      };

      return (
        <Stack gap={0}>
          <Group justify="space-between" p="xs">
            <Group>
              <Text size="sm" c="dimmed">
                {selectedCount} selected
              </Text>
              {loadingMore && (
                <Text size="sm" c="dimmed">
                  Loading more
                </Text>
              )}
            </Group>
            <Group gap="xs" wrap="nowrap">
              {toolbarActions
                .filter((action) => (action.isVisible ? action.isVisible(ctx) : true))
                .map((action) => (
                  <Button
                    key={action.id}
                    variant={action.variant || 'light'}
                    color={action.color}
                    size="xs"
                    disabled={action.isDisabled ? action.isDisabled(ctx) : selectedCount === 0}
                    leftSection={action.icon}
                    onClick={() => action.handler(medplum, selectedRefs, handleActionComplete)}
                  >
                    {action.label.replace('{count}', selectedCount.toString() || '')}
                  </Button>
                ))}
              {isTasksEnabled && inboxSortControl}
            </Group>
          </Group>
          <Divider />
        </Stack>
      );
    },
    enableRowActions: false,
    mantineTableBodyRowProps: ({ row }) => {
      const notification = row.original;
      return {
        onClick: () => {
          onRowClick(notification, medplum, handleRowClick);
        },
        style: {
          cursor: 'pointer',
          backgroundColor: getRowBackgroundColor(notification),
          borderBottom: '1px solid #e9ecef',
        },
      };
    },
  });

  return (
    <Paper style={{ flex: 1, overflow: 'hidden' }}>
      <MantineReactTable table={table} />
    </Paper>
  );
}
